import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Configuratore Box regalo (sections/main-box-product.liquid).
 *
 * Il cliente sceglie una bottiglia per ogni posizione della box. All'aggiunta al
 * carrello la box e le bottiglie diventano righe separate, legate da un codice di
 * riferimento comune, così più box nello stesso carrello restano distinguibili.
 *
 * L'aggiunta usa lo stesso protocollo degli add-to-cart di Horizon
 * (CartLinesUpdateEvent + sections), così cart drawer e contatore si aggiornano.
 */

// Alfabeto senza caratteri ambigui (0/O, 1/I/L) per i codici box
const REF_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
const REF_LENGTH = 5;

function generateBoxRef() {
  const values = crypto.getRandomValues(new Uint32Array(REF_LENGTH));
  return Array.from(values, (value) => REF_ALPHABET[value % REF_ALPHABET.length]).join('');
}

/**
 * Chiave di confronto di un elenco di categorie, indipendente da ordine e maiuscole:
 * ["Liquori", "Gin"] e "gin, liquori" danno entrambi "gin|liquori".
 * @param {unknown} categories
 */
function contentsKey(categories) {
  const list = Array.isArray(categories) ? categories : String(categories ?? '').split(',');
  return list
    .map((category) => String(category).trim().toLowerCase())
    .filter(Boolean)
    .sort()
    .join('|');
}

class BoxConfigurator extends HTMLElement {
  /** @type {Map<string, {variantId: string, price: number, title: string, category: string}>} */
  #selection = new Map();
  #busy = false;
  #config = {};

  connectedCallback() {
    try {
      this.#config = JSON.parse(this.querySelector('[data-box-config]')?.textContent || '{}');
    } catch (error) {
      console.error('Box configurator — configurazione non valida:', error);
      return;
    }

    // Posizioni fisse (un solo prodotto nella lista): già selezionate
    this.querySelectorAll('[data-box-option][data-fixed]').forEach((option) => {
      if (option instanceof HTMLButtonElement) this.#select(option);
    });

    this.addEventListener('click', this.#onClick);
    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** Posizioni mostrate in pagina, in ordine (una per lista prodotti con bottiglie) */
  get #slots() {
    return Array.from(this.querySelectorAll('fieldset[data-slot]'), (element) =>
      element instanceof HTMLElement ? element.dataset.slot || '' : ''
    ).filter(Boolean);
  }

  get #capacity() {
    return this.#slots.length;
  }

  get #isComplete() {
    return this.#capacity > 0 && this.#selection.size === this.#capacity;
  }

  /**
   * Variante della box da usare: a scelta completa quella il cui custom.contenuto
   * corrisponde alle categorie delle bottiglie scelte, altrimenti la predefinita.
   * @returns {{id: number, price: number, available: boolean} | undefined}
   */
  get #boxVariant() {
    /** @type {{id: number, price: number, available: boolean, contents: unknown}[]} */
    const variants = this.#config.variants || [];
    const fallback = variants.find((variant) => variant.id === this.#config.defaultVariantId) || variants[0];
    if (!this.#isComplete) return fallback;

    const key = contentsKey(Array.from(this.#selection.values(), (bottle) => bottle.category));
    return variants.find((variant) => contentsKey(variant.contents) === key) || fallback;
  }

  get #labels() {
    return this.#config.labels || {};
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    const option = target.closest('[data-box-option]');
    if (option instanceof HTMLButtonElement && !option.disabled && !option.hasAttribute('data-fixed')) {
      this.#toggleOption(option);
      return;
    }

    const arrow = target.closest('[data-scroll]');
    if (arrow instanceof HTMLElement) {
      const track = arrow.closest('.box-cfg__gallery, .box-cfg__carousel')?.querySelector('[data-track]');
      if (track) {
        const direction = Number(arrow.dataset.scroll) || 1;
        track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: 'smooth' });
      }
      return;
    }

    if (target.closest('[data-box-add]')) {
      this.#addToCart();
    }
  };

  /** @param {HTMLButtonElement} option */
  #toggleOption(option) {
    const slot = option.dataset.slot;
    if (!slot) return;

    const current = this.#selection.get(slot);
    if (current && current.variantId === option.dataset.variantId) {
      // Secondo click sulla stessa bottiglia: deseleziona
      this.#selection.delete(slot);
    } else {
      this.#select(option);
    }

    this.#setStatus('');
    this.#render();
  }

  /** @param {HTMLButtonElement} option */
  #select(option) {
    const slot = option.dataset.slot;
    if (!slot) return;
    this.#selection.set(slot, {
      variantId: option.dataset.variantId || '',
      price: Number(option.dataset.price) || 0,
      title: option.dataset.title || '',
      category: option.dataset.category || '',
    });
  }

  #render() {
    const labels = this.#labels;

    // Stato delle card e riepilogo di ogni posizione
    this.querySelectorAll('[data-slot]').forEach((slotElement) => {
      if (!(slotElement instanceof HTMLFieldSetElement)) return;
      const slot = slotElement.dataset.slot || '';
      const chosen = this.#selection.get(slot);

      slotElement.querySelectorAll('[data-box-option]').forEach((option) => {
        if (!(option instanceof HTMLElement)) return;
        const pressed = Boolean(chosen && chosen.variantId === option.dataset.variantId);
        option.setAttribute('aria-pressed', String(pressed));
      });

      const choice = slotElement.querySelector('[data-slot-choice]');
      if (choice) choice.textContent = chosen ? chosen.title : labels.empty || '';
    });

    // Totale = prezzo della variante box (dipende dalla combinazione) + bottiglie scelte
    const boxVariant = this.#boxVariant;
    const boxPrice = Number(boxVariant?.price) || 0;
    const total = boxPrice + Array.from(this.#selection.values()).reduce((sum, bottle) => sum + bottle.price, 0);
    const money = (/** @type {number} */ cents) =>
      formatMoney(cents, this.#config.moneyFormat || '{{amount}}', this.#config.currency);

    const totalElement = this.querySelector('[data-box-total]');
    // Finché la box non è completa il totale non è definitivo (dipende dalla combinazione)
    if (totalElement) totalElement.textContent = this.#isComplete ? money(total) : '—';

    const complete = this.#isComplete;
    const addButton = this.querySelector('[data-box-add]');
    if (addButton instanceof HTMLButtonElement) {
      addButton.disabled =
        !complete || this.#busy || !boxVariant?.available || addButton.hasAttribute('data-sold-out');
    }

    const hint = this.querySelector('[data-box-hint]');
    if (hint instanceof HTMLElement) hint.hidden = complete;
  }

  /**
   * @param {string} message
   * @param {boolean} [isError]
   */
  #setStatus(message, isError = false) {
    const status = this.querySelector('[data-box-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('box-cfg__status--error', isError);
  }

  #buildItems() {
    const labels = this.#labels;
    const ref = generateBoxRef();

    // Bottiglie in ordine di posizione
    const bottles = this.#slots.map((slot) => this.#selection.get(slot)).filter(Boolean);

    const boxProperties = {
      [labels.propContents || 'Bottiglie']: bottles.map((bottle) => bottle.title).join(' + '),
      [labels.propRef || 'Rif. box']: ref,
    };

    // La stessa bottiglia scelta in più posizioni diventa una sola riga con quantità > 1
    /** @type {Map<string, number>} */
    const bottleQuantities = new Map();
    for (const bottle of bottles) {
      bottleQuantities.set(bottle.variantId, (bottleQuantities.get(bottle.variantId) || 0) + 1);
    }

    return [
      { id: Number(this.#boxVariant?.id), quantity: 1, properties: boxProperties },
      ...Array.from(bottleQuantities, ([variantId, quantity]) => ({
        id: Number(variantId),
        quantity,
        properties: { [labels.propRef || 'Rif. box']: ref },
      })),
    ];
  }

  async #addToCart() {
    if (this.#busy || !this.#isComplete) return;

    const labels = this.#labels;
    const items = this.#buildItems();
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    // Sezioni del carrello (drawer / pagina carrello) da ri-renderizzare
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    this.#busy = true;
    this.#setStatus('');
    this.#render();

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    const routes = window.Theme?.routes || {};

    try {
      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw Object.assign(new Error(data.description || data.message || 'Add to cart failed'), { data });
      }

      const cart = await fetch(`${routes.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      }).then((res) => res.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'box-configurator',
          sourceId: this.id || 'box-configurator',
          itemCount,
          sections: data.sections,
          didError: false,
        },
      });

      this.#setStatus(labels.added || '');
    } catch (error) {
      console.error('Box configurator — errore aggiunta al carrello:', error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: error?.data?.description, errors: error?.data?.errors },
        })
      );
      // Anche in errore si passa il carrello reale: il contatore del carrello lo legge dall'evento
      const cart = await fetch(`${routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })
        .then((res) => res.json())
        .catch(() => null);
      deferred.resolve({
        cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
        detail: { didError: true, items: cart?.items, source: 'box-configurator', itemCount: 0 },
      });

      // Il messaggio di Shopify (es. quantità non disponibile) è più utile di quello generico
      this.#setStatus(error?.data?.description || labels.error || '', true);
    } finally {
      this.#busy = false;
      this.#render();
    }
  }
}

if (!customElements.get('box-configurator')) {
  customElements.define('box-configurator', BoxConfigurator);
}
